/* =========================================================================
 * Kkumi · Engagement (for Kkumi Oracle's pixel)
 * -------------------------------------------------------------------------
 * How each page went, which Oracle's first-party pixel can't see from inside
 * Shopify's sandbox (docs/inventory.md → First-party pixel → How the first
 * page went). Per page view:
 *   s  seconds the page was on screen, as the last mark passed: 0, then 5,
 *      15, 30, 60, 120, 300. Counted from the tap that opened the page (or
 *      from when a prerendered page was shown), only while the tab is
 *      visible; it stops at 5 minutes.
 *   i  1 once the shopper scrolled, tapped or typed (a person, not a
 *      preloaded page nobody looked at).
 *   p  1 once the price came into view while the page was on screen
 *   b  1 once the main Add to cart came into view (elements marked
 *      data-kk-px-seen="price" / "buy": today the product hero's price panel
 *      and Add to cart slot; the phone's sticky bar doesn't count).
 *   o  1 once the shopper picked a product option themselves (a real tap on
 *      an option card, select or Simple Bundles choice; the page's own
 *      automatic picks don't count, the rule the product hero uses).
 *   f  ms until the page first showed something (first contentful paint)
 *   l  ms until its largest image or text block showed (largest contentful
 *      paint; Chrome and Android only, never iPhones).
 *
 * Publishes one Shopify custom event, kkumi:engagement, with the whole state
 * so far each time it moves (changes within a quarter second go as one), so
 * a lost event costs nothing and their order doesn't matter. Oracle's pixel
 * (apps/oracle-pixel) forwards it; nothing else listens. Shopify keeps events
 * published before its pixels load and hands them over once they do, so
 * publish is looked up on every send, never kept.
 * Honours the shopper's cookie choices (the cart-seen rule: analytics and
 * marketing, the two Oracle's pixel needs). Never throws.
 * No visible UI and no copy: nothing here is shown to the shopper.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__kkEngagement) return;
  window.__kkEngagement = true;
  if (!window.performance || typeof performance.now !== 'function') return;

  var EVENT = 'kkumi:engagement';
  var MARKS = [5, 15, 30, 60, 120, 300];
  var CAP_MS = 60000;
  var state = { s: 0, i: 0, p: 0, b: 0, o: 0, f: null, l: null };
  var sent = '';
  var pending = null;

  // Cookie choices: when Shopify's consent API is on the page and the shopper hasn't allowed analytics or
  // marketing, nothing is published. Where no choice is asked (Australia) the API allows both.
  function allowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return true;
    try {
      if (typeof cp.analyticsProcessingAllowed === 'function' && !cp.analyticsProcessingAllowed()) return false;
      if (typeof cp.marketingAllowed === 'function' && !cp.marketingAllowed()) return false;
    } catch (e) {}
    return true;
  }
  function send() {
    pending = null;
    var data = {};
    for (var k in state) if (state[k] != null) data[k] = state[k];
    var sig = JSON.stringify(data);
    if (sig === sent || !allowed()) return;
    try {
      var an = window.Shopify && window.Shopify.analytics;
      if (!an || typeof an.publish !== 'function') return;
      var out = an.publish(EVENT, data);
      sent = sig;
      if (out && typeof out.then === 'function') out.then(null, function () {});
    } catch (e) {}
  }
  function changed() { if (!pending) pending = setTimeout(send, 250); }

  /* ---- on-screen time ---- */
  // When the page was shown: the tap that opened it, or the moment a prerendered page was swapped in
  // (activationStart is only known from then, so it is read each time).
  function start() {
    try { var n = performance.getEntriesByType('navigation')[0]; return (n && n.activationStart) || 0; } catch (e) { return 0; }
  }
  function onScreen() { return document.visibilityState === 'visible' && !document.prerendering; }
  var shown = 0;
  var since = onScreen() ? start() : null;
  var tick = null;
  function seconds() { return (shown + (since == null ? 0 : performance.now() - since)) / 1000; }
  function step() {
    tick = null;
    var e = seconds(), s = state.s, k;
    for (k = 0; k < MARKS.length; k++) if (e >= MARKS[k]) s = MARKS[k];
    if (s !== state.s) { state.s = s; changed(); }
    if (since == null) return;
    for (k = 0; k < MARKS.length; k++) {
      if (MARKS[k] > s) { tick = setTimeout(step, Math.max(0, (MARKS[k] - e) * 1000) + 50); return; }
    }
  }
  function visibility() {
    if (onScreen()) { if (since == null) since = performance.now(); }
    else if (since != null) { shown += performance.now() - since; since = null; }
    if (tick) clearTimeout(tick);
    step();
    look();
  }
  document.addEventListener('visibilitychange', visibility);
  document.addEventListener('prerenderingchange', visibility);

  /* ---- paint ---- */
  function ms(t) { return Math.min(CAP_MS, Math.max(0, Math.round(t - start()))); }
  function fcp(entries) {
    for (var j = 0; j < entries.length; j++) {
      if (entries[j].name === 'first-contentful-paint' && state.f == null) { state.f = ms(entries[j].startTime); changed(); }
    }
  }
  try { fcp(performance.getEntriesByType('paint')); } catch (e) {}
  if (state.f == null && typeof PerformanceObserver === 'function') {
    try { new PerformanceObserver(function (list) { fcp(list.getEntries()); }).observe({ type: 'paint', buffered: true }); } catch (e) {}
  }
  // The largest paint can still grow; it rides along with the next change instead of sending on its own.
  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver(function (list) {
        var es = list.getEntries(), last = es[es.length - 1];
        if (last) state.l = ms(last.renderTime || last.startTime);
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {}
  }

  /* ---- a person: scrolled, tapped or typed ---- */
  var TOUCH = ['pointerdown', 'keydown', 'wheel', 'touchmove'];
  function touched(e) {
    if (e && e.isTrusted === false) return;
    for (var q = 0; q < TOUCH.length; q++) window.removeEventListener(TOUCH[q], touched, true);
    if (!state.i) { state.i = 1; changed(); }
  }
  for (var t = 0; t < TOUCH.length; t++) window.addEventListener(TOUCH[t], touched, { capture: true, passive: true });

  /* ---- a product option picked by the shopper ---- */
  var PICKERS = 'variant-selects, [data-kk-opt-rows], [data-kk-sb]';
  function picked(e) {
    var el = e.target;
    if (state.o || !e.isTrusted || !el || !el.closest || !el.closest(PICKERS)) return;
    if (e.type === 'click' ? !el.closest('label, input') : !(el.type === 'radio' || el.tagName === 'SELECT')) return;
    state.o = 1;
    changed();
  }
  document.addEventListener('change', picked, true);
  document.addEventListener('click', picked, true);

  /* ---- the price and the main Add to cart, seen ---- */
  var marked = [];
  function look() {
    if (!onScreen()) return;
    for (var j = 0; j < marked.length; j++) {
      if (!marked[j].kkIn) continue;
      var k = marked[j].getAttribute('data-kk-px-seen');
      if (k === 'price' && !state.p) { state.p = 1; changed(); }
      if (k === 'buy' && !state.b) { state.b = 1; changed(); }
    }
  }
  function watch() {
    if (!('IntersectionObserver' in window)) return;
    marked = Array.prototype.slice.call(document.querySelectorAll('[data-kk-px-seen="price"], [data-kk-px-seen="buy"]'));
    if (!marked.length) return;
    var io = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) entries[j].target.kkIn = entries[j].isIntersecting;
      look();
    }, { threshold: 0.5 });
    for (var j = 0; j < marked.length; j++) io.observe(marked[j]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
  step();
})();
